import { type FC, useState, useEffect, useMemo } from "react";
import { Select, Input, Tooltip, Popover, Form } from "antd";
import { InfoCircleOutlined, DownOutlined } from "@ant-design/icons";
import type { ColumnsType, SorterResult } from "antd/es/table/interface";
import type { Dayjs } from "dayjs";

import { AppStrings } from "../../../utils/constants/AppStrings";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import {
  BRAND_ICONS_URLS,
  KPI_CARD_BACKGROUND,
} from "../../../utils/Constants";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import styles from "./SportsBetsManualActions.module.css";
import SettlementBadge from "../../shared/SettlementBadge/SettlementBadge";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import AgentPerformanceView from "../AgentPerformanceView/AgentPerformanceView";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { getManualActionsStaffManagementHref } from "../manualActionsStaffDeepLink";
import { convertToUserTimezone } from "../../../utils/dateService";
import CustomAutoCompleteControlled from "../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";
import {
  useGetAALSportsBetsTable,
  type SportsBetsAALTableItem,
  type SportsBetsAALTableFilters,
} from "../../../queries/agent-audit-log/sports-bets/useGetAALSportsBetsTable";
import { useGetAALSportsBetsKPIs } from "../../../queries/agent-audit-log/sports-bets/useGetAALSportsBetsKPIs";
import {
  cascadingOptionsFor,
  filterOptionsFor,
  type AalFilterMeta,
} from "../../../queries/agent-audit-log/aalV2";
import { useDownloadAALSportsBetsTable } from "../../../queries/agent-audit-log/sports-bets/useDownloadAALSportsBetsTable";
import { useDownloadOnTrigger } from "../../../hooks/useDownloadOnTrigger";
import { toAalSort } from "../manualActionsTableShared";
import { useGetRoles } from "../../../queries/useRolesApi";
import { useGetStaff } from "../../../queries/useGetStaff";
import UserAvatar from "../../CommonComponents/UserAvatar/UserAvatar";

// Cast JS components to typed FCs
type AutoCompleteOption = { value: string; label: string; id: string };
type CustomAutoCompleteProps = {
  allOptions: AutoCompleteOption[];
  selector: string;
  value: string;
  handleChange: (val: string) => void;
  clearOptions: () => void;
  placeholder?: string;
  onSearchChange?: (val: string) => void;
};
const TypedAutoComplete =
  CustomAutoCompleteControlled as unknown as FC<CustomAutoCompleteProps>;

type DataTableProps = {
  columns: ColumnsType<SportsBetsAALTableItem>;
  dataSource: SportsBetsAALTableItem[];
  loading: boolean;
  onChange: (
    p: unknown,
    f: unknown,
    s:
      | SorterResult<SportsBetsAALTableItem>
      | SorterResult<SportsBetsAALTableItem>[],
  ) => void;
  rowKey: (record: SportsBetsAALTableItem) => string;
  scroll: { x: number; y: number };
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, size: number) => void;
  };
};
const TypedDataTable = DataTable as unknown as FC<DataTableProps>;

type Role = {
  id: string;
  name: string;
  color: string;
  is_system_role: boolean;
  has_all_permissions: boolean;
  in_use: boolean;
  created_at: string;
  updated_at: string;
  actions: string[];
  permissions: string[];
};

interface SportsBetsManualActionsProps {
  agentPerformanceView?: boolean;
  dates?: [Dayjs, Dayjs] | null;
  showFilters?: boolean;
  downloadTrigger?: number;
}

type PanelFilters = {
  action_type: string | null;
  bet_type: string | null;
  bet_settlement: string | null;
  bet_money_type: string | null;
  agent: string;
  role: string | null;
  username: string;
};

type BetTypeSelectOption =
  | { value: string; label: string }
  | { label: string; options: { value: string; label: string }[] };

const INITIAL_PANEL_FILTERS: PanelFilters = {
  action_type: null,
  bet_type: null,
  bet_settlement: null,
  bet_money_type: null,
  agent: "",
  role: null,
  username: "",
};

/**
 * Maps Ant Design column sort `field` to backend `sort_by`.
 * Detail table: `created_at` (the "Date&Time" column) → `date`.
 */
export function sportsBetsSortFieldForApi(field: string): string | null {
  if (field === "created_at") return "date";
  if (field === "bet_turnover") return "turnover";
  if (field === "bet_pnl") return "pnl";
  if (field === "bet_wager_amount" || field === "bet_payout") return null;
  return field;
}

function buildBetTypeSelectOptions(
  filtersMeta: readonly AalFilterMeta[],
  betTypeLabels: Record<string, string>,
): BetTypeSelectOption[] {
  const flatOptions = filterOptionsFor(filtersMeta, "bet_type");
  const cascading = cascadingOptionsFor(filtersMeta, "bet_type");
  const childValues = new Set(Object.values(cascading).flat());
  const localize = (value: string, label: string) => ({
    value,
    label: betTypeLabels[value.toLowerCase()] ?? label,
  });

  const result: BetTypeSelectOption[] = [];
  for (const option of flatOptions) {
    if (childValues.has(option.value)) continue;
    const children = cascading[option.value];
    if (children?.length) {
      result.push({
        label: localize(option.value, option.label).label,
        options: children.map((childValue) => {
          const childOption = flatOptions.find((o) => o.value === childValue);
          return localize(
            childValue,
            childOption?.label ?? childValue,
          );
        }),
      });
      continue;
    }
    result.push(localize(option.value, option.label));
  }
  return result;
}

const BET_TYPE_LABELS: Record<string, string> = {
  straight: AppStrings.STRAIGHT,
  sportsbook: AppStrings.STRAIGHT,
  parlay: AppStrings.PARLAY,
  sgp: AppStrings.CREATE_TASK_BET_TYPE_SGP,
  flex: AppStrings.SPORTSBOOK_AAL_BET_TYPE_FLEX,
  group: AppStrings.GROUP,
  round_robin: AppStrings.ROUND_ROBIN,
  peer: AppStrings.PEER,
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  LEG_SETTLED: AppStrings.LEG_SETTLED,
  BET_SETTLED: AppStrings.BET_SETTLED,
  BET_CANCELLED: AppStrings.BET_CANCELLED,
  BET_ROLLED_BACK: AppStrings.BET_ROLLED_BACK,
};

const MONEY_TYPE_LABELS: Record<string, string> = {
  p_cash: AppStrings.PIKKEM_CASH,
  casino_cash: AppStrings.CASINO_CASH,
  rebet_cash: AppStrings.PRIMARY_CURRENCY,
  rebet_coin: AppStrings.SECONDARY_CURRENCY,
};

function SportsBetsManualActions({
  agentPerformanceView = false,
  dates,
  showFilters,
  downloadTrigger = 0,
}: SportsBetsManualActionsProps) {
  const [form] = Form.useForm();
  const watchedAgent = Form.useWatch("agent", form) ?? "";
  const watchedActionType = Form.useWatch("action_type", form);
  const watchedBetType = Form.useWatch("bet_type", form);
  const watchedBetSettlement = Form.useWatch("bet_settlement", form);
  const watchedBetMoneyType = Form.useWatch("bet_money_type", form);
  const watchedRole = Form.useWatch("role", form);
  const watchedUsername = Form.useWatch("username", form);

  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  const [isAgentDirty, setIsAgentDirty] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<PanelFilters>(
    INITIAL_PANEL_FILTERS,
  );

  const isDirty =
    (watchedActionType ?? null) !== appliedFilters.action_type ||
    (watchedBetType ?? null) !== appliedFilters.bet_type ||
    (watchedBetSettlement ?? null) !== appliedFilters.bet_settlement ||
    (watchedBetMoneyType ?? null) !== appliedFilters.bet_money_type ||
    isAgentDirty ||
    (watchedRole ?? null) !== appliedFilters.role ||
    (watchedUsername ?? "") !== appliedFilters.username;

  // APV role — changes refetch immediately (no Apply needed)
  const [selectedRole, setSelectedRole] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null);

  const start_date = dates?.[0]?.utc().format("YYYY-MM-DD");
  const end_date = dates?.[1]?.utc().format("YYYY-MM-DD");
  const listView = agentPerformanceView ? "agents" : "events";

  const tableFilters: SportsBetsAALTableFilters = {
    view: listView,
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    page: currentPage,
    size: pageSize,
    sort: toAalSort(sportsBetsSortFieldForApi(sortField ?? ""), sortOrder),
    ...(agentPerformanceView
      ? selectedRole.length > 0 && { agent_role_id: selectedRole[0] }
      : appliedFilters.role && { agent_role_id: appliedFilters.role }),
    ...(!agentPerformanceView &&
      appliedFilters.action_type && { action_type: appliedFilters.action_type }),
    ...(!agentPerformanceView &&
      appliedFilters.bet_type && { bet_type: appliedFilters.bet_type }),
    ...(!agentPerformanceView &&
      appliedFilters.bet_settlement && {
        bet_settlement: appliedFilters.bet_settlement,
      }),
    ...(!agentPerformanceView &&
      appliedFilters.bet_money_type && {
        bet_money_type: appliedFilters.bet_money_type,
      }),
    ...(!agentPerformanceView &&
      appliedFilters.agent && { agent_id: appliedFilters.agent }),
    ...(!agentPerformanceView &&
      appliedFilters.username && { username: appliedFilters.username }),
  };

  const kpiFilters = {
    start_date: start_date ?? "",
    end_date: end_date ?? "",
  };

  const {
    items: tableItems,
    total: totalResults,
    filters: tableFiltersMeta,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
  } = useGetAALSportsBetsTable(tableFilters);

  const betTypeOptions = useMemo(
    () => buildBetTypeSelectOptions(tableFiltersMeta, BET_TYPE_LABELS),
    [tableFiltersMeta],
  );
  const actionTypeOptions = useMemo(
    () =>
      filterOptionsFor(tableFiltersMeta, "action_type").map((option) => ({
        ...option,
        label: ACTION_TYPE_LABELS[option.value] ?? option.label,
      })),
    [tableFiltersMeta],
  );
  const betSettlementOptions = useMemo(
    () => filterOptionsFor(tableFiltersMeta, "bet_settlement"),
    [tableFiltersMeta],
  );
  const betMoneyTypeOptions = useMemo(
    () =>
      filterOptionsFor(tableFiltersMeta, "bet_money_type").map((option) => ({
        ...option,
        label: MONEY_TYPE_LABELS[option.value] ?? option.label,
      })),
    [tableFiltersMeta],
  );
  const flatBetTypeOptions = useMemo(
    () =>
      betTypeOptions.flatMap((option) =>
        "options" in option ? option.options : [option],
      ),
    [betTypeOptions],
  );

  const { data: kpiData, isLoading: isKpiLoading } =
    useGetAALSportsBetsKPIs(kpiFilters);
  const { data: roles } = useGetRoles() as { data: Role[] | undefined };
  const { data: staffData } = useGetStaff({ page_size: 100 });

  const agentOptions = useMemo(
    () =>
      (staffData?.items ?? []).map((s) => ({
        value: s.email,
        label: s.email,
        id: s.id,
      })),
    [staffData],
  );

  const roleOptions = useMemo(
    () => (roles ?? []).map((r) => ({ value: r.id, label: r.name })),
    [roles],
  );

  const roleById = useMemo(
    () =>
      Object.fromEntries(
        (roles ?? []).map((r) => [
          r.id,
          { name: r.name, color: r.color ?? "#6b7280" },
        ]),
      ),
    [roles],
  );

  // Go back to page 1 when APV role changes
  useEffect(() => {
    if (agentPerformanceView) {
      setCurrentPage(1);
    }
  }, [selectedRole, agentPerformanceView]);

  // Clean up when toggling between normal view and APV
  useEffect(() => {
    if (!agentPerformanceView) {
      setSelectedRole([]);
    }
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  }, [agentPerformanceView]);

  // Download — fires when the parent bumps downloadTrigger
  const { start: downloadTable } = useDownloadAALSportsBetsTable(listView);
  useDownloadOnTrigger(downloadTrigger, downloadTable, () => ({
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    ...(agentPerformanceView
      ? selectedRole.length > 0
        ? { agent_role_id: selectedRole[0] }
        : {}
      : {
          ...(appliedFilters.bet_type && {
            bet_type: appliedFilters.bet_type,
          }),
          ...(appliedFilters.action_type && {
            action_type: appliedFilters.action_type,
          }),
          ...(appliedFilters.bet_settlement && {
            bet_settlement: appliedFilters.bet_settlement,
          }),
          ...(appliedFilters.bet_money_type && {
            bet_money_type: appliedFilters.bet_money_type,
          }),
          ...(appliedFilters.agent && { agent_id: appliedFilters.agent }),
          ...(appliedFilters.role && { agent_role_id: appliedFilters.role }),
          ...(appliedFilters.username && {
            username: appliedFilters.username,
          }),
        }),
  }));

  const handleClearFilters = () => {
    form.resetFields();
    form.setFieldsValue({ agent: "" });
    setAutoCompleteKey((k) => k + 1);
    setIsAgentDirty(false);
    setAppliedFilters(INITIAL_PANEL_FILTERS);
    setCurrentPage(1);
  };

  const handleApply = () => {
    const values = form.getFieldsValue();
    setAppliedFilters({
      action_type: values.action_type ?? null,
      bet_type: values.bet_type ?? null,
      bet_settlement: values.bet_settlement ?? null,
      bet_money_type: values.bet_money_type ?? null,
      agent: form.getFieldValue("agent") ?? "",
      role: values.role ?? null,
      username: values.username ?? "",
    });
    setIsAgentDirty(false);
    setCurrentPage(1);
  };

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter:
      | SorterResult<SportsBetsAALTableItem>
      | SorterResult<SportsBetsAALTableItem>[],
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s.field) {
      setSortField(s.field as string);
      setSortOrder(s.order ?? null);
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const formatCurrency = (value: number | string, percentage = false) =>
    new Intl.NumberFormat("en-US", {
      style: percentage ? "percent" : "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value ?? 0));

  const renderPercent = (percent: number) => {
    const sign = percent >= 0 ? "+" : "";
    return (
      <span
        style={{
          color: getColorBasedOnValue(percent),
          fontSize: "11px",
          marginLeft: "4px",
        }}
      >
        {sign}
        {percent.toFixed(1)}%
      </span>
    );
  };

  // For multileg bets the backend returns pipe-separated values (e.g. "Team to Win|Over/Under")
  const renderPipeSeparatedCell = (text: string) => {
    if (!text) return "—";
    const items = text.split("|").map((s) => s.trim());
    if (items.length <= 1) return <span>{text}</span>;

    const content = (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          maxHeight: 200,
          overflowY: "auto",
        }}
      >
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              padding: "4px 8px",
              borderRadius: 4,
              backgroundColor: "var(--grey-750)",
              fontSize: 12,
            }}
          >
            {item}
          </div>
        ))}
      </div>
    );

    return (
      <Popover content={content} trigger="click" placement="bottomLeft">
        <span
          style={{
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {items[0]}
          <span
            style={{
              fontSize: 11,
              color: "var(--orange-500)",
              fontWeight: 500,
            }}
          >
            +{items.length - 1} <DownOutlined style={{ fontSize: 9 }} />
          </span>
        </span>
      </Popover>
    );
  };

  // ---- Normal view table columns ----
  const columns: ColumnsType<SportsBetsAALTableItem> = [
    {
      title: AppStrings.DATE_AND_TIME,
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      showSorterTooltip: false,
      width: 180,
      render: (text: string) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: AppStrings.ACTION_TYPE,
      dataIndex: "action_type",
      key: "action_type",
      width: 150,
      render: (value: string) => {
        const label = ACTION_TYPE_LABELS[value];
        return label ?? value ?? "—";
      },
    },
    {
      title: AppStrings.BET_ID,
      dataIndex: "bet_id",
      key: "bet_id",
      width: 150,
    },
    {
      title: AppStrings.BET_TYPE,
      dataIndex: "bet_type",
      key: "bet_type",
      width: 130,
      render: (value: string) => {
        const normalized = value?.toLowerCase();
        const label =
          BET_TYPE_LABELS[normalized] ??
          flatBetTypeOptions.find(
            (option) => option.value.toLowerCase() === normalized,
          )?.label;
        return label ?? value ?? "—";
      },
    },
    {
      title: AppStrings.MARKET,
      dataIndex: "bet_market_name",
      key: "bet_market_name",
      width: 180,
      render: (text: string) => renderPipeSeparatedCell(text),
    },
    {
      title: AppStrings.SELECTION,
      dataIndex: "bet_selection_name",
      key: "bet_selection_name",
      width: 180,
      render: (text: string) => renderPipeSeparatedCell(text),
    },
    {
      title: AppStrings.MONEY_TYPE,
      dataIndex: "bet_money_type",
      key: "bet_money_type",
      width: 130,
      render: (value: string) => MONEY_TYPE_LABELS[value] ?? value,
    },
    {
      title: AppStrings.PREMATCH_LIVE,
      dataIndex: "bet_timing",
      key: "bet_timing",
      width: 110,
    },
    {
      title: AppStrings.WAGER,
      dataIndex: "bet_wager_amount",
      key: "bet_wager_amount",
      showSorterTooltip: false,
      width: 160,
      render: (text: number) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
        </span>
      ),
    },
    {
      title: AppStrings.PAYOUT,
      dataIndex: "bet_payout",
      key: "bet_payout",
      showSorterTooltip: false,
      width: 160,
      render: (text: number) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
        </span>
      ),
    },
    {
      title: AppStrings.SETTLEMENT_STATUS,
      dataIndex: "bet_settlement",
      key: "bet_settlement",
      width: 150,
      render: (text: string) => <SettlementBadge status={text} />,
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "target_username",
      key: "target_username",
      width: 150,
      render: (text: string, record) => (
        <a
          href={`${NAVIGATION_ROUTES.USER_MANAGER}?userId=${record.target_user_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.sportsBetsManualActionsHoverable}
        >
          <UserAvatar />
          {text}
        </a>
      ),
    },
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      width: 180,
      render: (text: string, record) => (
        <a
          href={getManualActionsStaffManagementHref(record.agent_id)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.sportsBetsManualActionsHoverable}
        >
          {text}
        </a>
      ),
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 150,
      render: (value: string) => {
        const role = roleById[value];
        if (!role) return <span>{value || "—"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
    // {
    //   title: AppStrings.REASON,
    //   dataIndex: "reason",
    //   key: "reason",
    //   width: 300,
    // },
  ];

  // ---- Agent Performance View columns ----
  const agentPerformanceColumns: ColumnsType<Record<string, unknown>> = [
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      width: 200,
      render: (text: string) => (
        <a
          href={getManualActionsStaffManagementHref()}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.sportsBetsManualActionsHoverable}
        >
          {text}
        </a>
      ),
    },
    {
      title: AppStrings.TURNOVER,
      dataIndex: "bet_turnover",
      key: "bet_turnover",
      width: 220,
      sorter: true,
      render: (text: number, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["bet_turnover_pct_change"] != null &&
            renderPercent(record["bet_turnover_pct_change"] as number)}
        </span>
      ),
    },
    {
      title: AppStrings.PROFIT_LOSS,
      dataIndex: "bet_pnl",
      key: "bet_pnl",
      width: 220,
      sorter: true,
      render: (text: number, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["bet_pnl_pct_change"] != null &&
            renderPercent(record["bet_pnl_pct_change"] as number)}
        </span>
      ),
    },
    {
      title: AppStrings.SPORTS_BETS_SETTLED_TITLE,
      dataIndex: "bets_settled_count",
      key: "bets_settled_count",
      width: 200,
      sorter: true,
      render: (text: number, record) => (
        <span>
          {text ?? 0}
          {record["bets_settled_count_pct_change"] != null &&
            renderPercent(record["bets_settled_count_pct_change"] as number)}
        </span>
      ),
    },
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {AppStrings.USER_REALIZED_VALUE}
          <Tooltip title={AppStrings.USER_REALIZED_VALUE_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "amount_realized",
      key: "amount_realized",
      width: 220,
      sorter: true,
      render: (text: number, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="currency"
            style={{ width: "14px", height: "14px" }}
          />
          {formatCurrency(text || 0)}
          {record["amount_realized_pct_change"] != null &&
            renderPercent(record["amount_realized_pct_change"] as number)}
        </span>
      ),
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 170,
      render: (value: string) => {
        const role = roleById[value];
        if (!role) return <span>{value || "—"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.sportsBetsManualActionsContainer}>
      <Form form={form}>
        <div
          className={styles.sportsBetsManualActionsSelectors}
          style={{
            display: showFilters && !agentPerformanceView ? "flex" : "none",
          }}
        >
          <div className={styles.sportsBetsManualActionsSelector}>
            <span>{AppStrings.ACTION_TYPE}</span>
            <Form.Item name="action_type" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={actionTypeOptions}
                aria-label={AppStrings.ACTION_TYPE}
              />
            </Form.Item>
          </div>
          <div className={styles.sportsBetsManualActionsSelector}>
            <span>{AppStrings.BET_TYPE}</span>
            <Form.Item name="bet_type" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={betTypeOptions}
                aria-label={AppStrings.BET_TYPE}
              />
            </Form.Item>
          </div>
          <div className={styles.sportsBetsManualActionsSelector}>
            <span>{AppStrings.SETTLEMENT_STATUS}</span>
            <Form.Item name="bet_settlement" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={betSettlementOptions}
                aria-label={AppStrings.SETTLEMENT_STATUS}
              />
            </Form.Item>
          </div>
          <div className={styles.sportsBetsManualActionsSelector}>
            <span>{AppStrings.MONEY_TYPE}</span>
            <Form.Item name="bet_money_type" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={betMoneyTypeOptions}
                aria-label={AppStrings.MONEY_TYPE}
              />
            </Form.Item>
          </div>
          <div className={styles.sportsBetsManualActionsSelector}>
            <span>{AppStrings.PAM_AGENT}</span>
            <TypedAutoComplete
              key={autoCompleteKey}
              allOptions={agentOptions}
              selector="id"
              value={watchedAgent}
              handleChange={(id: string) => {
                form.setFieldValue("agent", id);
                setIsAgentDirty(true);
              }}
              clearOptions={() => form.setFieldValue("agent", "")}
              placeholder={AppStrings.NOT_SELECTED}
            />
          </div>
          <div className={styles.sportsBetsManualActionsSelector}>
            <span>{AppStrings.ROLE}</span>
            <Form.Item name="role" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={roleOptions}
              />
            </Form.Item>
          </div>
          <div className={styles.sportsBetsManualActionsSelector}>
            <span>{AppStrings.USERNAME}</span>
            <Form.Item name="username" noStyle>
              <Input
                placeholder="Text"
                className={styles.sportsBetsManualActionsFilterInput}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div
        className={styles.sportsBetsManualActionsSelectorActionButtons}
        style={{
          display: showFilters && !agentPerformanceView ? "flex" : "none",
        }}
      >
        <button className={styles.clear} onClick={handleClearFilters}>
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          className={isDirty ? `${styles.apply} ${styles.dirty}` : styles.apply}
          onClick={handleApply}
        >
          {AppStrings.APPLY}
        </button>
      </div>

      {/* KPI Cards */}
      <div
        className={styles.sportsBetsManualActionsKpiCards}
        style={{ display: !agentPerformanceView ? "flex" : "none" }}
      >
        <div className={styles.kpiRow}>
          <div className={styles.kpiCardWrapper}>
            <KpiCard
              title={AppStrings.TURNOVER.toUpperCase()}
              value={kpiData?.turnover ?? 0}
              hugeValue={false}
              percentage={kpiData?.turnover_pct_change}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={2}
            />
          </div>
          <div className={styles.kpiCardWrapper}>
            <KpiCard
              title={AppStrings.PROFIT_LOSS.toUpperCase()}
              value={kpiData?.pnl ?? 0}
              hugeValue={false}
              percentage={kpiData?.pnl_pct_change}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={2}
            />
          </div>
          <div className={styles.kpiCardWrapper}>
            <KpiCard
              title={AppStrings.HASH_OF_BETS.toUpperCase()}
              value={kpiData?.bets_settled_count ?? 0}
              hugeValue={false}
              percentage={kpiData?.bets_settled_count_pct_change}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={0}
            />
          </div>
          <div className={styles.kpiCardWrapper}>
            <Tooltip
              title={AppStrings.USER_REALIZED_VALUE_TOOLTIP}
              placement="top"
            >
              <InfoCircleOutlined className={styles.kpiInfoIcon} />
            </Tooltip>
            <KpiCard
              title={AppStrings.USER_REALIZED_VALUE.toUpperCase()}
              value={kpiData?.amount_realized ?? 0}
              hugeValue={false}
              percentage={kpiData?.amount_realized_pct_change}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={2}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {agentPerformanceView ? (
        <AgentPerformanceView
          columns={agentPerformanceColumns}
          handleTableChange={handleTableChange}
          filteredData={tableItems}
          roleOptions={roleOptions}
          selectedRoles={selectedRole}
          onRoleChange={setSelectedRole}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            onChange: (page: number, size: number) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      ) : (
        <TypedDataTable
          columns={columns}
          dataSource={tableItems}
          loading={isTableLoading || isTableFetching}
          onChange={handleTableChange}
          rowKey={(record) => record.event_id}
          scroll={{ x: 2800, y: 500 }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      )}
    </div>
  );
}

export default SportsBetsManualActions;
